import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './Autocomplete.module.css';

const Autocomplete = ({
  options = [],
  value,
  onChange,
  onInputChange,
  placeholder = 'Type to search...',
  icon: Icon,
  error,
  label,
  disabled = false,
  getOptionLabel = (option) => option.name || option,
  getOptionValue = (option) => option.id || option,
  filterFunction = (options, inputValue) => {
    const lower = inputValue.toLowerCase();
    return options.filter((opt) => {
      const label = getOptionLabel(opt).toLowerCase();
      return label.includes(lower);
    });
  }
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Log when options change
  React.useEffect(() => {
    if (label) {
      console.log(`Autocomplete "${label}" - options updated. Count: ${options?.length || 0}`, options);
    }
  }, [options, label]);

  // Log when props change
  useEffect(() => {
    console.log(`Autocomplete "${placeholder}" - disabled: ${disabled}, options: ${options?.length || 0}, isOpen: ${isOpen}`);
  }, [disabled, options?.length, isOpen, placeholder]);

  // Update filtered options when input changes
  useEffect(() => {
    if (inputValue) {
      const filtered = filterFunction(options, inputValue);
      setFilteredOptions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      // When input is empty, show all options
      setFilteredOptions(options);
    }
  }, [inputValue, options, filterFunction]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onInputChange?.(newValue);
    // Show dropdown when user types
    if (newValue && newValue.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    // When input is focused, show all options
    if (options && options.length > 0) {
      console.log('Autocomplete focus - showing all options');
      setFilteredOptions(options);
      setIsOpen(true);
    }
  };

  const handleSelectOption = (option) => {
    onChange(option);
    setInputValue(''); // Clear input value after selection
    setIsOpen(false);
  };

  // Show selected value label in input, or input text if user is typing
  const displayValue = inputValue || (value ? getOptionLabel(value) : '');

  return (
    <div className={styles.container} ref={containerRef}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.wrapper}>
        {Icon && <Icon className={styles.icon} size={18} />}
        <input
          ref={inputRef}
          type="text"
          className={`${styles.input} ${Icon ? styles.padLeft : ''} ${error ? styles.error : ''}`}
          placeholder={placeholder}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          autoComplete="off"
          aria-label={label}
        />
        <ChevronDown 
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} 
          size={16} 
        />
      </div>

      {isOpen && filteredOptions && filteredOptions.length > 0 && (
        <div className={styles.dropdown}>
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className={styles.option}
              onClick={() => handleSelectOption(option)}
            >
              {getOptionLabel(option)}
            </div>
          ))}
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

export default Autocomplete;
